import { ApolloClient } from '@apollo/client/core/core.cjs';
import { HttpLink } from '@apollo/client/link/http';
//import { BatchHttpLink } from '@apollo/client/link/batch-http/batch-http.cjs';
import { createToaster } from '@melt-ui/svelte';
import { derived, type Readable, type Writable, writable } from 'svelte/store';
import { createCache } from '$lib/ApolloCache.js';
import { DpApHelper } from '$lib/DpAp/DpApHelper';
import { UrlHelper } from '$lib/UrlHelper.js';
import type { WrapData } from '$lib/apollo/ApolloData';
import { ContainerClosable } from '$lib/container/ContainerClosable';
import { CountryHelper } from '$lib/country/CountryHelper.js';
import { IntlUtils } from '$lib/intl.js';
import { MercureManager } from '$lib/mercure/MercureManager';
import { GCaptchaHelper } from '$lib/recaptcha/GCaptchaHelper';
import type { SvelteClient } from '$lib/svelteClient/SvelteClient';
import { UploadApi } from '$lib/upload/UploadApi.js';
import { getMessageFromError } from '$lib/utils/error';
import singleton from '$lib/utils/factory.js';
import type { CurrentUser_MeQueryQuery } from '$gql/graphql';
import { CurrentUserStore } from '$components/CurrentUserStore/CurrentUserStore';
import { getDatalayer } from '$components/GtmDatalayer/gtm';
import { InboxStore } from '$components/Inbox/InboxStore';
import { SearchHeaderStore } from '$components/SearchHeader/SearchHeaderStore';
import type { ToastData } from '$components/Toast/ToastData';

export class ShareableContainer extends ContainerClosable {
    public readonly svelteClient: SvelteClient;
    locale: Writable<string>;
    lang: Readable<string>;

    constructor(
        public readonly parameters: {
            svelteClient: SvelteClient;
            browser: boolean;
            goafricaDomain: string;
            mercureBaseUrl: string;
            initialCurrentUserId: number | null;
            initialCurrentUserData: WrapData<CurrentUser_MeQueryQuery> | null;
            currentCountry: string | null;
            recaptchaSiteKey: string;
            searchHeader: ConstructorParameters<typeof SearchHeaderStore>[0];
            gtmId: string;
            leaflet: {
                tileLayerAttribution: string;
                tileLayerUrl: string;
            };
            facebookAppId: string;
            initialLocale: string;
        },
    ) {
        super();
        this.svelteClient = this.parameters.svelteClient;
        this.locale = writable(this.parameters.initialLocale);
        this.lang = derived(this.locale, ($value) => {
            return $value.substring(0, 2);
        });
    }

    browser = () => {
        return this.parameters.browser;
    };

    urlHelper = singleton(() => {
        return new UrlHelper(
            this.countryHelper(),
            this.parameters.goafricaDomain,
            () => this.dpapHelper(),
            false,
        );
    });

    intl = singleton(() => {
        return new IntlUtils();
    });

    countryHelper = singleton(() => {
        return new CountryHelper(this.intl(), this.parameters.currentCountry);
    });

    dpapHelper = singleton(() => {
        return new DpApHelper(this.intl());
    });

    apollo = singleton(() => {
        return new ApolloClient({
            cache: createCache(),
            devtools: {
                enabled: true,
            },
            link: new HttpLink({
                uri: '/graphql-next',
                credentials: 'include',
                //batchMax: 1,
                //batchInterval: 1,
            }),
        });
    });

    uploadApi = singleton(() => {
        return new UploadApi(`https://www.${this.parameters.goafricaDomain}`);
    });

    toaster = singleton(() => {
        return createToaster<ToastData>();
    });

    autoErrorToast = async <T>(cb: () => Promise<T>): Promise<T> => {
        const {
            helpers: { addToast },
        } = this.toaster();

        try {
            return await cb();
        } catch (err) {
            addToast({
                data: {
                    type: 'error',
                    message: getMessageFromError(err),
                },
            });

            throw err;
        }
    };

    mercureManager = singleton(() => {
        return new MercureManager(this.parameters.mercureBaseUrl, this.browser(), () =>
            this.currentUserStore(),
        );
    });

    currentUserStore = singleton(() => {
        const store = new CurrentUserStore(
            this.parameters.initialCurrentUserId,
            this.parameters.initialCurrentUserData,
            {
                apollo: () => this.apollo(),
                browser: () => this.browser(),
            },
        );

        this.registerCloseCallback(1, async () => {
            store.close();
        });

        return store;
    });

    inboxStore = singleton(() => {
        return new InboxStore(
            () => this.apollo(),
            () => this.browser(),
            () => this.mercureManager(),
        );
    });

    recaptchaHelper = singleton(() => {
        return new GCaptchaHelper(this.parameters.recaptchaSiteKey);
    });

    searchHeaderStore = singleton(() => {
        return new SearchHeaderStore(this.parameters.searchHeader);
    });

    datalayer = singleton(() => {
        return getDatalayer(this.parameters.gtmId);
    });
}
